<template>
  <v-main>
    <v-container fluid>
      <!-- Filter Section -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Marketing Campaigns</h3>
              <v-btn
                class="mx-2 mb-2"
                fab
                x-small
                color="primary"
                to="/marketing-campaigns/create"
              >
                <v-icon dark>mdi-plus</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="3">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search campaigns..."
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col md="2">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.status"
                    :items="statusOptions"
                    clearable
                    label="Status"
                    @input="getData"
                    prepend-inner-icon="mdi-filter"
                  ></v-autocomplete>
                </v-col>
                <v-col md="2">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.schedule_type"
                    :items="scheduleTypeOptions"
                    clearable
                    label="Schedule Type"
                    @input="getData"
                    prepend-inner-icon="mdi-clock-outline"
                  ></v-autocomplete>
                </v-col>
                <v-col md="2">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.channel"
                    :items="channelOptions"
                    clearable
                    label="Channel"
                    @input="getData"
                    prepend-inner-icon="mdi-send"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table Section -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </section>
              <v-simple-table v-else fixed-header height="500" class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left" style="min-width: 60px">Sr No</th>
                      <th class="text-left" style="min-width: 200px">Campaign Name</th>
                      <th class="text-left" style="min-width: 100px">Status</th>
                      <th class="text-left" style="min-width: 150px">Channels</th>
                      <th class="text-left" style="min-width: 180px">Schedule</th>
                      <th class="text-left" style="min-width: 120px">Stats</th>
                      <th class="text-right" style="min-width: 180px">Actions</th>
                    </tr>
                  </thead>
                  <tbody v-if="campaigns.length != 0">
                    <tr
                      v-for="(campaign, i) in campaigns"
                      :key="`campaign_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                      }"
                    >
                      <td>{{ serialNoStarting + i + 1 }}</td>
                      <td>
                        <strong>{{ campaign.name }}</strong>
                        <div class="text-caption grey--text">
                          {{ campaign.message | truncate(50) }}
                        </div>
                      </td>
                      <td>
                        <v-chip
                          small
                          :color="getStatusColor(campaign.status)"
                          text-color="white"
                        >
                          {{ campaign.status | capitalize }}
                        </v-chip>
                      </td>
                      <td>
                        <v-chip
                          v-for="channel in campaign.channels"
                          :key="channel"
                          small
                          class="mr-1 mb-1"
                          :color="getChannelColor(channel)"
                          text-color="white"
                        >
                          <v-icon small left>{{ getChannelIcon(channel) }}</v-icon>
                          {{ channel | capitalize }}
                        </v-chip>
                      </td>
                      <td>
                        <div>{{ campaign.schedule_display }}</div>
                        <div v-if="campaign.last_executed_at" class="text-caption grey--text">
                          Last: {{ campaign.last_executed_at | niceDate }}
                        </div>
                      </td>
                      <td>
                        <div class="text-caption">
                          <span class="success--text">Sent: {{ campaign.total_sent }}</span>
                          <br />
                          <span class="error--text">Failed: {{ campaign.total_failed }}</span>
                        </div>
                      </td>
                      <td class="text-right">
                        <!-- Send Now Button -->
                        <v-tooltip bottom v-if="campaign.status === 'draft'">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="success"
                              class="mr-1"
                              @click="sendCampaign(campaign.id)"
                              :loading="sendingId === campaign.id"
                            >
                              <v-icon dark>mdi-send</v-icon>
                            </v-btn>
                          </template>
                          <span>Send Now</span>
                        </v-tooltip>

                        <!-- Pause Button (for active recurring) -->
                        <v-tooltip bottom v-if="campaign.status === 'active' && campaign.schedule_type === 'recurring'">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="warning"
                              class="mr-1"
                              @click="pauseCampaign(campaign.id)"
                            >
                              <v-icon dark>mdi-pause</v-icon>
                            </v-btn>
                          </template>
                          <span>Pause Campaign</span>
                        </v-tooltip>

                        <!-- Resume Button (for paused) -->
                        <v-tooltip bottom v-if="campaign.status === 'paused'">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="success"
                              class="mr-1"
                              @click="resumeCampaign(campaign.id)"
                            >
                              <v-icon dark>mdi-play</v-icon>
                            </v-btn>
                          </template>
                          <span>Resume Campaign</span>
                        </v-tooltip>

                        <!-- View Logs -->
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="info"
                              class="mr-1"
                              :to="`/marketing-campaigns/${campaign.id}/logs`"
                            >
                              <v-icon dark>mdi-chart-bar</v-icon>
                            </v-btn>
                          </template>
                          <span>View Logs</span>
                        </v-tooltip>

                        <!-- Edit Button -->
                        <v-tooltip bottom v-if="campaign.status !== 'completed'">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="primary"
                              class="mr-1"
                              :to="`/marketing-campaigns/${encryptIt(campaign.id)}`"
                            >
                              <v-icon dark>mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit Campaign</span>
                        </v-tooltip>

                        <!-- Delete Button -->
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="error"
                              @click="askDeleteCampaign(campaign.id)"
                            >
                              <v-icon dark>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                          <span>Delete Campaign</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="7" class="text-center">No campaigns found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination rounded v-model="page" :length="paginationLength"></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="400">
        <v-card rounded="xxl">
          <v-card-text class="text-center pa-6">
            <v-icon large color="success" style="font-size: 60px">mdi-check-circle</v-icon>
            <h3 class="mt-4">{{ successMessage }}</h3>
            <v-btn color="success" class="mt-4" @click="showSuccessDialog = false">OK</v-btn>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Delete confirmation -->
      <ConfirmationDialog
        :show.sync="deleteDialog"
        title="Delete Campaign"
        message="Are you sure you want to delete this campaign? This action cannot be undone."
        icon="mdi-delete"
        color="error"
        :loading="isDeletingCampaign"
        @confirm="deleteCampaign(pendingDeleteId)"
      />
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import ConfirmationDialog from "@/components/ConfirmationDialog.vue";

export default {
  name: "MarketingCampaignsPage",
  components: { ConfirmationDialog },
  data() {
    return {
      filter: {
        search_keyword: "",
        status: "",
        schedule_type: "",
        channel: "",
      },
      campaigns: [],
      page: 1,
      serialNoStarting: 0,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      sendingId: null,
      showSuccessDialog: false,
      successMessage: "",
      deleteDialog: false,
      pendingDeleteId: null,
      isDeletingCampaign: false,
      statusOptions: [
        { text: "Draft", value: "draft" },
        { text: "Scheduled", value: "scheduled" },
        { text: "Active", value: "active" },
        { text: "Paused", value: "paused" },
        { text: "Completed", value: "completed" },
        { text: "Cancelled", value: "cancelled" },
      ],
      scheduleTypeOptions: [
        { text: "Immediate", value: "immediate" },
        { text: "Scheduled", value: "scheduled" },
        { text: "Recurring", value: "recurring" },
      ],
      channelOptions: [
        { text: "Push Notification", value: "push" },
        { text: "SMS", value: "sms" },
        { text: "WhatsApp", value: "whatsapp" },
      ],
    };
  },
  filters: {
    truncate(value, length) {
      if (!value) return "";
      if (value.length <= length) return value;
      return value.substring(0, length) + "...";
    },
    capitalize(value) {
      if (!value) return "";
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        let query = new URLSearchParams({
          page: this.page,
          rowsPerPage: this.rowsPerPage,
          search_keyword: this.filter.search_keyword || "",
          status: this.filter.status || "",
          schedule_type: this.filter.schedule_type || "",
          channel: this.filter.channel || "",
        }).toString();

        const response = await axios.get(`marketing-campaigns?${query}`);
        this.campaigns = response.data.data;
        this.count = response.data.count;
        this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (error) {
        console.error("Failed to fetch campaigns:", error);
      }
      this.isLoading = false;
    },

    getStatusColor(status) {
      const colors = {
        draft: "grey",
        scheduled: "blue",
        active: "green",
        paused: "orange",
        completed: "teal",
        cancelled: "red",
      };
      return colors[status] || "grey";
    },

    getChannelColor(channel) {
      const colors = {
        push: "purple",
        sms: "indigo",
        whatsapp: "green",
      };
      return colors[channel] || "grey";
    },

    getChannelIcon(channel) {
      const icons = {
        push: "mdi-bell",
        sms: "mdi-message-text",
        whatsapp: "mdi-whatsapp",
      };
      return icons[channel] || "mdi-send";
    },

    async sendCampaign(id) {
      this.sendingId = id;
      try {
        await axios.post("marketing-campaigns/send", { campaign_id: id });
        this.successMessage = "Campaign queued for sending!";
        this.showSuccessDialog = true;
        this.getData();
      } catch (error) {
        console.error("Failed to send campaign:", error);
        alert("Failed to send campaign: " + (error.response?.data?.message || error.message));
      }
      this.sendingId = null;
    },

    async pauseCampaign(id) {
      try {
        await axios.post("marketing-campaigns/pause", { campaign_id: id });
        this.successMessage = "Campaign paused successfully!";
        this.showSuccessDialog = true;
        this.getData();
      } catch (error) {
        console.error("Failed to pause campaign:", error);
      }
    },

    async resumeCampaign(id) {
      try {
        await axios.post("marketing-campaigns/resume", { campaign_id: id });
        this.successMessage = "Campaign resumed successfully!";
        this.showSuccessDialog = true;
        this.getData();
      } catch (error) {
        console.error("Failed to resume campaign:", error);
      }
    },

    askDeleteCampaign(id) {
      this.pendingDeleteId = id;
      this.deleteDialog = true;
    },
    async deleteCampaign(id) {
      this.isDeletingCampaign = true;
      try {
        await axios.post(`marketing-campaigns/delete/${this.encryptIt(id)}`);
        this.deleteDialog = false;
        this.successMessage = "Campaign deleted successfully!";
        this.showSuccessDialog = true;
        this.getData();
      } catch (error) {
        console.error("Failed to delete campaign:", error);
      } finally {
        this.isDeletingCampaign = false;
      }
    },
  },
};
</script>
